<script setup>
import {computed} from 'vue';
import {fractionalToDecimal} from "@/services/oddsConverterServiceFunctions";

// Define props
const props = defineProps({
  bet: Object
});

// Computed property to format the date and time separately
const formattedDate = computed(() => {
  const date = new Date(props.bet.eventStart);
  return date.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
});

const formattedTime = computed(() => {
  const date = new Date(props.bet.eventStart);
  return date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit'
  });
});

// Determine the sport icon (football or horse)
const sportIcon = computed(() => {
  return props.bet.sport === 'football' ? 'football-icon-class' : 'horse-icon-class';
});

// Format the implied odds to two decimal places
const impliedOddsFormatted = computed(() => {
  return props.bet.implied_odds.toFixed(2);
});
</script>


<template>
  <div class="extra-place-matcher">
    <!-- Event Information -->
    <div class="event-info">
      <div class="event-date-time">
        <span><i class="bi bi-calendar"></i> {{ formattedDate }}</span>
        <span><i class="bi bi-clock"></i> {{ formattedTime }}</span>
      </div>
      <div class="event-description">
        <i :class="sportIcon"></i> {{ bet.eventDescription }}
      </div>
    </div>

    <!-- Selection -->
    <div class="selection">{{ bet.selection }}</div>

    <!-- Back and Lay Bets -->
    <div class="bets-row">
      <!-- Back Bet -->
      <div class="bet-column">
        <div class="bet-type">Back Bet</div>
        <div class="bet-details">
          <div class="bet-bookie">{{ bet.backBet.bookieDisplay }}</div>
          <div class="bet-odds">Win odds: {{ bet.backBet.win_odds }}</div>
          <div class="bet-odds">Place odds: {{ bet.backBet.place_odds }}</div>
          <div class="bet-places">Places paid: {{ bet.backBet.places }}</div>
        </div>
      </div>
      <!-- Lay Bet -->
      <div class="bet-column">
        <div class="bet-type">Lay Bet</div>
        <div class="bet-details">
          <div class="bet-bookie">{{ bet.layBet.bookieDisplay }}</div>
          <div class="bet-odds">Win odds: {{ bet.layBet.win_odds }}</div>
          <div class="bet-odds">Place odds: {{ bet.layBet.place_odds }}</div>
          <div class="bet-places">Places paid: {{ bet.layBet.places }}</div>
        </div>
      </div>
    </div>

    <!-- Implied odds and Calculator Button -->
    <div class="bottom-row">
      <div class="implied-odds">
        Implied Odds: {{ impliedOddsFormatted }}
      </div>
      <router-link
          :to="{
               name: 'mb-calc-extra-place',
               query: {
                 backOdds: bet.backBet.win_odds,
                 backStake: 50,
                 ewTerms: fractionalToDecimal(bet.backBet.ew_terms),
                 layWinOdds: bet.layBet.win_odds,
                 layPlaceOdds: bet.layBet.place_odds,
               }
             }"
          class="calc-button">
        <i class="bi bi-calculator"></i> <span class="sm-only">Open Calc</span>
      </router-link>
    </div>
  </div>
</template>


<style scoped>
/* Styles for the Extra Place Matcher Component */

.extra-place-matcher {
  padding: 20px;
  border: 1px solid lightgrey;
  border-radius: 10px;
  background-color: #f9f9f9;
  margin: 20px auto;
}

.event-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: var(--fs-400);
  margin-inline: 1rem;
}

.event-date-time {
  display: flex;
  flex-direction: column;
}

.event-date-time i {
  margin-right: 5px;
}

.event-date-time i,
.event-date-time span {
  margin-right: 15px;
}

.event-description {
  display: flex;
  align-items: center;
  font-size: 2rem;
}

.selection {
  text-align: center;
  font-size: 1.5em;
  margin-bottom: 20px;
}

.bets-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}

.bet-column {
  flex: 1;
  margin: 0 10px;
}

.bet-type {
  font-weight: bold;
  margin-bottom: 10px;
}

.bet-details {
  background-color: #e9ecef;
  padding: 10px;
  border-radius: 5px;
}

.bet-bookie {
  font-weight: bold;
  margin-bottom: 5px;
}

.bet-odds,
.bet-places {
  margin-bottom: 5px;
}

.bottom-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-inline: 1rem;
}

.implied-odds {
  font-size: 1.2em;
  font-weight: bold;
}

.calc-button {
  background-color: var(--clr-primary-100);
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 5px;
  cursor: pointer;
  text-decoration: none;
}

.calc-button:hover {
  background-color: var(--clr-primary-40);
}

/* Sport Icon Classes */
.football-icon-class {
  background-image: url('@/assets/football-icon.svg');
  width: 36px;
  height: 36px;
  display: inline-block;
  background-size: cover;
  margin-right: 25px;
}

.horse-icon-class {
  background-image: url('@/assets/horse-icon.svg');
  width: 36px;
  height: 36px;
  display: inline-block;
  background-size: cover;
  margin-right: 25px;
}
</style>
